import { useWtrSelector } from 'redux/hooks';
import { getProductById } from 'redux/modules/entities/selectors/products';
import { getProductFindingMethod } from 'utils/get-product-finding-method';
import { useContext } from 'react';
import CarouselContext from 'components/Carousel/CarouselContext';
import {
  type AdMetadata,
  EventType,
  metadataAdImpressions,
  sendAdTechEvent,
} from 'analytics/gtmServerFunctions';
import { addProductImpression } from 'analytics/product-impressions-tracking';
import { Product, Promotion } from 'constants/products';
import { getPromotionsByIds } from 'redux/modules/entities/selectors/promotions';
import { ListAnalytics } from 'analytics/types';
import { getIsFavourite } from 'redux/modules/favourites-products/selectors';
import { usePodObserver } from './use-pod-observer';

const calculateDiscount = (
  currentSaleUnitPrice?: { price: { amount: number } },
  displayPrice?: string,
) => {
  const displayPriceFloat = displayPrice
    ? parseFloat(displayPrice.replace(/[^0-9.-]+/g, '')) / (displayPrice.includes('p') ? 100 : 1)
    : undefined;
  const calculatedDiscount =
    currentSaleUnitPrice && displayPriceFloat
      ? +((currentSaleUnitPrice.price.amount || 0) - displayPriceFloat).toFixed(2)
      : undefined;

  const getDiscount = () => {
    if (calculatedDiscount === undefined) {
      return undefined;
    }
    if (calculatedDiscount < 0) {
      return 0;
    }
    return calculatedDiscount;
  };

  return {
    discount: getDiscount(),
    price: displayPriceFloat,
  };
};

export const getPricingAndOfferDetails = (
  { currentSaleUnitPrice: currentPrice, displayPrice }: Partial<Product> = {},
  promotions?: Promotion[],
) => {
  const currencyCode = currentPrice?.price.currencyCode || '';
  let price = currentPrice?.price.amount || 0;
  const uom = currentPrice?.quantity?.uom || '';
  const visiblePromotion = promotions?.find((p: { hidden?: boolean }) => !p.hidden);
  const offer = visiblePromotion ? visiblePromotion.promotionDescription : undefined;
  const offerId = visiblePromotion ? visiblePromotion.promotionId : undefined;
  const priceWithDiscount =
    (promotions && visiblePromotion?.promotionUnitPrice?.amount) || undefined;

  let discount = price && priceWithDiscount ? +(price - priceWithDiscount).toFixed(2) : undefined;
  if (discount === undefined) {
    const { discount: calculatedDiscount, price: calculatedPrice } = calculateDiscount(
      currentPrice,
      displayPrice,
    );
    discount = calculatedDiscount;
    if (calculatedPrice !== undefined) {
      price = calculatedPrice;
    }
  }

  const finalPrice = priceWithDiscount || price;
  return { currencyCode, discount, price: finalPrice, offer, offerId, uom };
};

interface PodContext {
  metadata?: AdMetadata;
  position: number;
  productId: string;
  searchType: string | null;
  sponsorshipId?: string;
  trackingUrls?: string[];
  vendor?: string;
  wrapperId?: string;
  listDefinitions?: ListAnalytics;
}

export function usePodImpression(
  {
    metadata,
    position,
    productId,
    sponsorshipId,
    trackingUrls,
    vendor,
    listDefinitions,
  }: PodContext,
  sponsored?: boolean,
) {
  const product = useWtrSelector(state =>
    // @ts-expect-error entities selectors are not typed
    getProductById(state, productId),
  );
  const { productFindingMethod: carouselProductFindingMethod } = useContext(CarouselContext);
  const promotions = useWtrSelector(state =>
    // @ts-expect-error entities selectors are not typed
    getPromotionsByIds(state, product?.promotions || []),
  );
  const { currencyCode, discount, offer, offerId, price, uom } = getPricingAndOfferDetails(
    product,
    promotions,
  );
  const { lineNumber, name, categories, brand, productType } = product ?? {};
  const isFavourite = useWtrSelector((state: WtrState) => getIsFavourite(state, lineNumber));
  const categoriesNames =
    categories?.map(({ name: categoryName }: { name: string }) => categoryName) || [];
  const productFindingMethod = carouselProductFindingMethod || getProductFindingMethod();

  return usePodObserver((element: HTMLElement) => {
    if (!product) {
      return;
    }

    sendAdTechEvent({
      eventType: EventType.IMPRESSION,
      trackingUrls,
      sponsorshipId,
      vendor,
    });

    if (metadata) {
      metadataAdImpressions(metadata);
    }

    addProductImpression({
      position,
      currencyCode,
      element,
      id: productId,
      lineNumber,
      name,
      offer,
      price,
      listDefinitions,
      productFindingMethod,
      sponsored,
      sponsorshipId,
      uom,
      categoriesNames,
      discount,
      offerId,
      brand,
      productType,
      isFavourite,
    });
  });
}
